import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
const SCRIPT_LOAD_TIMEOUT_MS = 10000; // 10 second timeout
const POLL_INTERVAL_MS = 50;
// Load the WAF CAPTCHA script dynamically
function loadCaptchaScript(abortSignal) {
    return new Promise((resolve, reject)=>{
        const scriptUrl = import.meta.env.VITE_WAF_CAPTCHA_SCRIPT_URL;
        if (!scriptUrl) {
            reject(new Error('CAPTCHA script URL not configured'));
            return;
        }
        // Check if already loaded
        if (window.AwsWafCaptcha) {
            resolve();
            return;
        }
        // Check if aborted before starting
        if (abortSignal?.aborted) {
            reject(new Error('Script loading aborted'));
            return;
        }
        const startTime = Date.now();
        let timeoutId = null;
        const cleanup = ()=>{
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
        };
        // Listen for abort signal
        abortSignal?.addEventListener('abort', ()=>{
            cleanup();
            reject(new Error('Script loading aborted'));
        });
        const checkLoaded = ()=>{
            if (abortSignal?.aborted) {
                cleanup();
                return;
            }
            if (window.AwsWafCaptcha) {
                cleanup();
                resolve();
                return;
            }
            if (Date.now() - startTime > SCRIPT_LOAD_TIMEOUT_MS) {
                cleanup();
                reject(new Error('CAPTCHA script load timeout'));
                return;
            }
            timeoutId = setTimeout(checkLoaded, POLL_INTERVAL_MS);
        };
        // Check if script is already in DOM
        const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
        if (existingScript) {
            checkLoaded();
            return;
        }
        // Load the script
        const script = document.createElement('script');
        script.src = scriptUrl;
        script.async = true;
        script.onload = ()=>{
            checkLoaded();
        };
        script.onerror = ()=>{
            cleanup();
            reject(new Error('Failed to load CAPTCHA script'));
        };
        document.head.appendChild(script);
    });
}
export function CaptchaModal({ isOpen, onClose, onSuccess, onError }) {
    const containerRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // Use refs for callbacks to avoid dependency issues
    const onSuccessRef = useRef(onSuccess);
    const onErrorRef = useRef(onError);
    // Keep refs updated
    useEffect(()=>{
        onSuccessRef.current = onSuccess;
        onErrorRef.current = onError;
    }, [
        onSuccess,
        onError
    ]);
    useEffect(()=>{
        if (!isOpen || !containerRef.current) return;
        const apiKey = import.meta.env.VITE_WAF_CAPTCHA_API_KEY;
        if (!apiKey) {
            const errorMsg = 'CAPTCHA configuration missing. Please try again later.';
            setError(errorMsg);
            setIsLoading(false);
            onErrorRef.current?.(errorMsg);
            return;
        }
        setIsLoading(true);
        setError(null);
        // Create abort controller for cleanup
        const abortController = new AbortController();
        let isMounted = true;
        // Load script and render CAPTCHA
        loadCaptchaScript(abortController.signal).then(()=>{
            if (!isMounted || !containerRef.current || !window.AwsWafCaptcha) return;
            // Clear previous content safely
            while(containerRef.current.firstChild){
                containerRef.current.removeChild(containerRef.current.firstChild);
            }
            window.AwsWafCaptcha.renderCaptcha(containerRef.current, {
                apiKey,
                onSuccess: (token)=>{
                    if (!isMounted) return;
                    setIsLoading(false);
                    onSuccessRef.current(token);
                },
                onError: (err)=>{
                    if (!isMounted) return;
                    setIsLoading(false);
                    setError(err.message);
                    onErrorRef.current?.(err.message);
                },
                onLoad: ()=>{
                    if (!isMounted) return;
                    setIsLoading(false);
                }
            });
        }).catch((err)=>{
            if (!isMounted) return;
            // Ignore abort errors
            if (err.message === 'Script loading aborted') return;
            setIsLoading(false);
            setError(err.message);
            onErrorRef.current?.(err.message);
        });
        // Cleanup function
        return ()=>{
            isMounted = false;
            abortController.abort();
        };
    }, [
        isOpen
    ]); // Only depend on isOpen - callbacks are accessed via refs
    if (!isOpen) return null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "fixed inset-0 z-50 flex items-center justify-center",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "absolute inset-0 bg-black bg-opacity-50",
                onClick: onClose
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "relative bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 z-10",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-between items-center mb-4",
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                className: "text-lg font-semibold text-gray-900",
                                children: "Security Verification"
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                onClick: onClose,
                                className: "text-gray-400 hover:text-gray-600",
                                children: /*#__PURE__*/ _jsx("svg", {
                                    className: "w-6 h-6",
                                    fill: "none",
                                    stroke: "currentColor",
                                    viewBox: "0 0 24 24",
                                    children: /*#__PURE__*/ _jsx("path", {
                                        strokeLinecap: "round",
                                        strokeLinejoin: "round",
                                        strokeWidth: 2,
                                        d: "M6 18L18 6M6 6l12 12"
                                    })
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-600 mb-4",
                        children: "Please complete the security check below to continue."
                    }),
                    isLoading && /*#__PURE__*/ _jsx("div", {
                        className: "flex justify-center py-8",
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "animate-spin rounded-full h-8 w-8 border-b-2 border-navy-90"
                        })
                    }),
                    error && /*#__PURE__*/ _jsx("div", {
                        className: "bg-red-50 border border-red-200 rounded-md p-4 mb-4",
                        children: /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-red-600",
                            children: error
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        ref: containerRef,
                        className: "flex justify-center min-h-[200px]"
                    })
                ]
            })
        ]
    });
}
export default CaptchaModal;
